import inherits from 'inherits';

import CommandInterceptor from 'diagram-js/lib/command/CommandInterceptor';

import { is, isLabel } from '../../../base/BaseServices';

import { getBBox } from 'diagram-js/lib/util/Elements';

import {
  assign,
  find
} from 'min-dash';

import { asTRBL } from 'diagram-js/lib/layout/LayoutUtil';

var HORIZONTAL_PARTICIPANT_PADDING = 20,
    VERTICAL_PARTICIPANT_PADDING = 20;

export var PARTICIPANT_BORDER_WIDTH = 30;

var HIGH_PRIORITY = 2000;


/**
 * BPMN-specific behavior for creating participants.
 */
export default function CreateParticipantAction(canvas, eventBus, modeling) {
  CommandInterceptor.call(this, eventBus);

  // fit participant
  eventBus.on(['create.start', 'shape.move.start'], HIGH_PRIORITY, function(event) {
    const { context } = event;
    const { shape } = context;
    const rootElement = canvas.getRootElement();
  
    if (!isParticipantMoveStart(shape, rootElement)) {
      return;
    }
  
    const children = getValidChildren(rootElement);
  
    if (!children.length) {
      return;
    }
  
    const childrenBBox = getBBox(children);
    const participantBounds = getParticipantBounds(shape, childrenBBox);
  
    assign(shape, participantBounds);
  
    context.createConstraints = getParticipantCreateConstraints(shape, childrenBBox);
  });
  
  function isParticipantMoveStart(shape, rootElement) {
    return is(shape, 'bpmn:Participant') &&
           is(rootElement, 'bpmn:Process') &&
           rootElement.children.length > 0;
  }
  
  function getValidChildren(rootElement) {
    return rootElement.children.filter(element =>
      !is(element, 'bpmn:Group') && !isLabel(element) && !isConnection(element)
    );
  }  

  // force hovering process when creating first participant
  eventBus.on('create.start', HIGH_PRIORITY, function(event) {
    const { context } = event;
    const { shape } = context;
    const rootElement = canvas.getRootElement();
    const rootElementGfx = canvas.getGraphics(rootElement);
  
    function ensureHoveringProcess(event) {
      event.element = rootElement;
      event.gfx = rootElementGfx;
    }
  
    if (isParticipantCreating(shape, rootElement)) {
      setupParticipantCreationListeners(ensureHoveringProcess);
    }
  });
  
  function isParticipantCreating(shape, rootElement) {
    return is(shape, 'bpmn:Participant') && is(rootElement, 'bpmn:Process');
  }
  
  function setupParticipantCreationListeners(ensureHoveringProcess) {
    eventBus.on('element.hover', HIGH_PRIORITY, ensureHoveringProcess);
  
    eventBus.once('create.cleanup', function() {
      eventBus.off('element.hover', ensureHoveringProcess);
    });
  }  

  function ensureCollaboration(context) {
    const parent = context.parent;
    const rootElement = canvas.getRootElement();
  
    const collaboration = isCollaboration(rootElement) ? rootElement : createCollaboration(context);
  
    context.parent = collaboration;
  }
  
  function isCollaboration(element) {
    return is(element, 'bpmn:Collaboration');
  }
  
  function createCollaboration(context) {
    const { parent } = context;
    const collaboration = modeling.makeCollaboration();
  
    if (is(parent, 'bpmn:Process')) {
      context.process = parent;
    }
  
    return collaboration;
  }
  

  // turn process into collaboration before adding participant
  this.preExecute('shape.create', function(context) {
    const parent = context.parent;
    const shape = context.shape;
  
    if (isParticipantInProcess(shape, parent)) {
      ensureCollaboration(context);
    }
  }, true);
  
  function isParticipantInProcess(participant, process) {
    return is(participant, 'bpmn:Participant') && is(process, 'bpmn:Process');
  }
  

  this.execute('shape.create', function(context) {
    const process = context.process;
    const shape = context.shape;
  
    if (process) {
      updateProcessReference(shape, process, context);
    }
  }, true);
  
  function updateProcessReference(shape, process, context) {
    const { businessObject } = shape;
    const { processRef } = businessObject;
  
    if (processRef) {
      context.oldProcessRef = processRef;
    }
  
    businessObject.processRef = process.businessObject;
  }
  

  this.revert('shape.create', function(context) {
    const process = context.process;
    const shape = context.shape;
  
    if (process) {
      revertProcessReference(shape, context.oldProcessRef);
    }
  }, true);
  
  function revertProcessReference(shape, oldProcessRef) {
    const { businessObject } = shape;
  
    if (oldProcessRef) {
      businessObject.processRef = oldProcessRef;
    } else {
      // If there was no old process reference, remove the current process reference
      delete businessObject.processRef;
    }
  }
  

  this.postExecute('shape.create', function(context) {
    const process = context.process;
    const shape = context.shape;
  
    if (process) {
      moveChildrenToParticipant(modeling, process, shape);
    }
  }, true);
  
  function moveChildrenToParticipant(modeling, process, participant) {
    const processChildren = process.children.slice();
  
    modeling.moveElements(processChildren, { x: 0, y: 0 }, participant);
  }
  

  // turn process into collaboration when creating participants
  this.preExecute('elements.create', HIGH_PRIORITY, function(context) {
    const elements = context.elements;
    const parent = context.parent;
  
    const participant = findAndSetupParticipant(context);
  
    if (participant) {
      reuseProcessForParticipant(parent, context);
    }
  }, true);
  
  function findAndSetupParticipant(context) {
    const elements = context.elements;
    const hasParticipants = findParticipant(elements);
  
    if (hasParticipants && is(context.parent, 'bpmn:Process')) {
      ensureCollaboration(context);
  
      return findParticipant(elements);
    }
  
    return null;
  }
  
  function reuseProcessForParticipant(parent, context) {
    const participant = findParticipant(context.elements);
  
    if (participant) {
      context.oldProcessRef = participant.businessObject.processRef;
      participant.businessObject.processRef = parent.businessObject;
    }
  }
  

  this.revert('elements.create', function(context) {
    const elements = context.elements;
    const process = context.process;
  
    if (process) {
      const participant = findParticipant(elements);
  
      // re-use process when creating the first participant
      if (participant) {
        participant.businessObject.processRef = context.oldProcessRef;
      }
    }
  }, true);
  

  this.postExecute('elements.create', function(context) {
    const elements = context.elements;
    const process = context.process;
  
    if (process) {
      const participant = findParticipant(elements);
  
      // move children from process to the first participant
      if (participant) {
        const processChildren = process.children.slice();
        modeling.moveElements(processChildren, { x: 0, y: 0 }, participant);
      }
    }
  }, true);  

}

CreateParticipantAction.$inject = [
  'canvas',
  'eventBus',
  'modeling'
];

inherits(CreateParticipantAction, CommandInterceptor);

// helpers //////////

function getParticipantBounds(shape, childrenBBox) {
  const paddedChildrenBBox = {
    width: childrenBBox.width + HORIZONTAL_PARTICIPANT_PADDING * 2 + PARTICIPANT_BORDER_WIDTH,
    height: childrenBBox.height + VERTICAL_PARTICIPANT_PADDING * 2
  };

  const width = Math.max(shape.width, paddedChildrenBBox.width);
  const height = Math.max(shape.height, paddedChildrenBBox.height);

  return {
    x: -width / 2,
    y: -height / 2,
    width,
    height
  };
}


function getParticipantCreateConstraints(shape, childrenBBox) {
  const { top, right, bottom, left } = asTRBL(childrenBBox);

  return {
    bottom: top + shape.height / 2 - VERTICAL_PARTICIPANT_PADDING,
    left: right - shape.width / 2 + HORIZONTAL_PARTICIPANT_PADDING,
    top: bottom - shape.height / 2 + VERTICAL_PARTICIPANT_PADDING,
    right: left + shape.width / 2 - HORIZONTAL_PARTICIPANT_PADDING - PARTICIPANT_BORDER_WIDTH
  };
}


function isConnection(element) {
  return Boolean(element.waypoints);
}


function findParticipant(elements) {
  return find(elements, function(element) {
    return is(element, 'bpmn:Participant');
  });
}
